import { useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { PenLine, ClipboardList, FileText } from 'lucide-react';
import { CreateFromTemplatePicker, FormPageLayout } from '@/components/layouts';
import { useEstimateTemplates } from '@/hooks/estimateTemplates/useEstimateTemplates';
import { useBusinessUnitScopeSelection } from '@/components/organisms/business-units/useBusinessUnitScopeSelection';
import { isBusinessUnitId } from '@/lib/business-units/businessUnitScope';
import { useDebounce } from '@/hooks/useDebounce';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { DEFAULT_ESTIMATE_LIST_FILTERS } from '@/components/organisms/estimates/support/estimate-list-filters';
import type { EstimateFulfillmentMode } from '@/types/estimate';
import { QuickEstimateForm } from './quick';
import { GuidedEstimateWizard } from './guided';
import { EstimateFormPage } from './form';

// ─── Route definition ────────────────────────────────────────────────────────

type EstimateNewSearch = {
  edit?: string;
  duplicate?: string;
  template?: string;
  manual?: string;
  quick?: string;
  guided?: string;
  contactId?: string;
  locationId?: string;
  leadId?: string;
  // NOT-560 — "Create estimate" on a one-off job lands here (not on a
  // server-prefilled draft): the operator picks manual or template and authors
  // the lines themselves. These ride through the picker into the form so the
  // saved estimate still links back to the job it was quoted for.
  sourceJobId?: string;
  fulfillmentMode?: EstimateFulfillmentMode;
  originAppointmentId?: string;
  /**
   * The Business Unit chosen on the picker screen. It narrowed the templates
   * offered there and is stamped onto the estimate this flow creates
   * (`selectedBusinessUnitId`). The picker requires it whenever the tenant has
   * business units; it is optional HERE only because a tenant without units is
   * never asked, and because a deep link may skip the picker.
   */
  businessUnitId?: string;
};

const FULFILLMENT_MODES: readonly EstimateFulfillmentMode[] = [
  'CURRENT_JOB',
  'FOLLOW_UP_JOB',
  'UNDECIDED',
];

export const Route = createFileRoute('/_dashboard/dashboard/estimates/new')({
  component: NewEstimateWrapper,
  validateSearch: (search: Record<string, unknown>): EstimateNewSearch => ({
    edit: typeof search.edit === 'string' ? search.edit : undefined,
    duplicate: typeof search.duplicate === 'string' ? search.duplicate : undefined,
    template: typeof search.template === 'string' ? search.template : undefined,
    manual: readModeFlag(search.manual),
    quick: readModeFlag(search.quick),
    guided: readModeFlag(search.guided),
    contactId: typeof search.contactId === 'string' ? search.contactId : undefined,
    locationId: typeof search.locationId === 'string' ? search.locationId : undefined,
    leadId: typeof search.leadId === 'string' ? search.leadId : undefined,
    sourceJobId: typeof search.sourceJobId === 'string' ? search.sourceJobId : undefined,
    fulfillmentMode: FULFILLMENT_MODES.includes(search.fulfillmentMode as EstimateFulfillmentMode)
      ? (search.fulfillmentMode as EstimateFulfillmentMode)
      : undefined,
    // Only meaningful with its job; the server refuses it alone.
    originAppointmentId:
      typeof search.sourceJobId === 'string' && typeof search.originAppointmentId === 'string'
        ? search.originAppointmentId
        : undefined,
    businessUnitId: isBusinessUnitId(search.businessUnitId) ? search.businessUnitId : undefined,
  }),
});

function readModeFlag(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (value === true) return '1';
  return undefined;
}

// ─── Wrapper ─────────────────────────────────────────────────────────────────

// Exported for the route test: `Route.component` is code-split, so reaching
// the screen through `Route` suspends on a chunk vitest never resolves.
export function NewEstimateWrapper() {
  const router = useRouter();
  const search = Route.useSearch();
  const pricingHidden = useIsFieldHidden('PRICING');

  if (pricingHidden) {
    return (
      <FormPageLayout
        title="Estimate creation unavailable"
        onBack={() =>
          void router.navigate({
            to: '/dashboard/estimates',
            search: DEFAULT_ESTIMATE_LIST_FILTERS,
          })
        }
      >
        <div className="mx-auto max-w-xl px-4 py-6">
          <div className="rounded-lg border bg-muted/40 p-4">
            <p className="font-medium">Estimate pricing is hidden for your role.</p>
            <p className="mt-1 text-sm text-muted-foreground">
              You can still review estimate status, customer details, and line-item descriptions
              from the Estimates list.
            </p>
          </div>
        </div>
      </FormPageLayout>
    );
  }

  // An existing record already answered the business-unit question.
  if (search.edit || search.duplicate) {
    return <NewEstimateFromSearch businessUnitId={search.businessUnitId} />;
  }
  if (search.template || search.manual || search.leadId) {
    return <GatedNewEstimateFromSearch />;
  }
  if (search.quick) {
    return <QuickEstimateForm prefillContactId={search.contactId} />;
  }
  if (search.guided) {
    return <GuidedEstimateWizard prefillContactId={search.contactId} />;
  }

  return <NewPickerScreen />;
}

// ─── Create-path gate ─────────────────────────────────────────────────────────

/**
 * The picker asks for the business unit before Manual or a template — but a
 * link can name those directly (`?manual=1`, `?template=…`, a lead's "Convert
 * to estimate" hands over `?manual=1&leadId=…`) and skip the screen that asks.
 * This is the same rule applied at the door: a create path with no usable unit
 * on a tenant that has units is sent to the picker, which keeps everything the
 * link carried (contact, lead, source job) and asks the one question first.
 *
 * Same exemptions as the picker so the gate is never an obstacle:
 *   • no business units → straight through, unscoped, as before;
 *   • exactly one unit  → it is the answer; go through with it stamped on;
 *   • an archived unit in the link is dropped by the scope hook and treated
 *     as no answer, since new work cannot start in a retired unit;
 *   • a failed unit fetch settles on "no units" — the operator can still
 *     create, rather than being locked out by a transient error.
 */
function GatedNewEstimateFromSearch() {
  const search = Route.useSearch();
  const { businessUnitId, hasBusinessUnits, isSettled } = useBusinessUnitScopeSelection(
    search.businessUnitId,
  );
  // "No units yet" is not "no units": render nothing rather than the form,
  // or a fast tenant would see a page the next render replaces.
  if (!isSettled) return null;
  if (hasBusinessUnits && businessUnitId === null) return <NewPickerScreen />;
  return <NewEstimateFromSearch businessUnitId={businessUnitId ?? undefined} />;
}

// ─── Picker Screen ────────────────────────────────────────────────────────────

function NewPickerScreen() {
  const router = useRouter();
  const currentSearch = Route.useSearch();
  const [templateSearch, setTemplateSearch] = useState('');
  const [templatePage, setTemplatePage] = useState(1);
  const debouncedTemplateSearch = useDebounce(templateSearch.trim(), 300);
  const templateSearchIsSettling = templateSearch.trim() !== debouncedTemplateSearch;
  const { businessUnitId, setBusinessUnitId, hasBusinessUnits, isSettled } =
    useBusinessUnitScopeSelection(currentSearch.businessUnitId);
  // NOT-542 — the business unit comes first. It decides which templates are
  // offered and which unit the estimate belongs to, so neither "Manual" nor a
  // template is reachable until it is chosen. The step is only asked of a
  // tenant that has units; until the unit list settles the gate stays closed
  // rather than letting a fast click through on "no units yet".
  // A deep-linked unit is held optimistically before the list arrives; it is
  // not an ANSWER until the list has verified it, so the step is given no
  // value until then and the gate stays closed — an archived unit in a shared
  // link must not open Manual for a frame. (The step shows a placeholder while
  // loading, so no control ever displays the withheld value.)
  const businessUnitStepShown = hasBusinessUnits || !isSettled;
  const businessUnitStepValue = isSettled ? businessUnitId : null;
  const businessUnitGateClosed = businessUnitStepShown && businessUnitStepValue === null;
  const {
    data: templatesData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useEstimateTemplates(
    {
      search: debouncedTemplateSearch || undefined,
      // Undefined ⇒ the key is omitted and every template is listed — which
      // only happens for a tenant without business units, since the gate
      // below holds the query off until a unit is chosen.
      businessUnitId: businessUnitId ?? undefined,
      limit: 50,
      page: templatePage,
    },
    { enabled: !businessUnitGateClosed },
  );
  const carry = {
    ...(currentSearch.contactId ? { contactId: currentSearch.contactId } : {}),
    ...(currentSearch.locationId ? { locationId: currentSearch.locationId } : {}),
    ...(currentSearch.leadId ? { leadId: currentSearch.leadId } : {}),
    ...(currentSearch.sourceJobId ? { sourceJobId: currentSearch.sourceJobId } : {}),
    ...(currentSearch.sourceJobId && currentSearch.fulfillmentMode
      ? { fulfillmentMode: currentSearch.fulfillmentMode }
      : {}),
    ...(currentSearch.sourceJobId && currentSearch.originAppointmentId
      ? { originAppointmentId: currentSearch.originAppointmentId }
      : {}),
    ...(businessUnitId ? { businessUnitId } : {}),
  };
  const templates = templatesData?.data ?? [];

  function goBack() {
    // @ts-expect-error — search required by TanStack Router v1.168
    void router.navigate({ to: '/dashboard/estimates' });
  }

  return (
    <CreateFromTemplatePicker
      title="New Estimate"
      description="Choose how to start"
      onBack={goBack}
      businessUnitStep={
        businessUnitStepShown
          ? {
              value: businessUnitStepValue,
              onChange: (value: string | null) => {
                // A narrower list renumbers: page 3 of "everything" is usually
                // past the end of one business unit's templates.
                setTemplatePage(1);
                setBusinessUnitId(value);
              },
              narrows: 'templates',
              required: true,
            }
          : undefined
      }
      scratchTitle="Manual / From Scratch"
      scratchDescription="Create the estimate yourself"
      scratchIcon={PenLine}
      scratchOptions={[
        {
          title: 'Manual',
          description: 'All fields — taxes, discount, deposit, T&C and more.',
          onClick: () =>
            void router.navigate({
              to: '/dashboard/estimates/new',
              search: { manual: '1', ...carry } as never,
            }),
          icon: ClipboardList,
        },
      ]}
      templateTitle="From Template"
      templateDescription="Start with saved line items and settings"
      templateIcon={FileText}
      isLoadingTemplates={isLoading || templateSearchIsSettling}
      isSearchingTemplates={isFetching && !isLoading}
      templateEmptyMessage="No templates yet. Create one from the Templates section."
      templateSearchValue={templateSearch}
      onTemplateSearchChange={(value) => {
        setTemplateSearch(value);
        setTemplatePage(1);
      }}
      templateSearchPlaceholder="Search estimate templates..."
      templateNoResultsMessage="No estimate templates match your search."
      templateLoadError={
        isError
          ? {
              message: 'Couldn’t load estimate templates.',
              onRetry: () => void refetch(),
            }
          : undefined
      }
      templatePagination={
        templatesData
          ? {
              page: templatesData.page,
              totalPages: templatesData.totalPages,
              total: templatesData.total,
              onPageChange: setTemplatePage,
            }
          : undefined
      }
      templateOptions={templates.map((template) => ({
        id: template.id,
        title: template.name,
        description: `${template.description ? `${template.description} · ` : ''}${(template.lineItems ?? []).length} items`,
        onClick: () =>
          void router.navigate({
            to: '/dashboard/estimates/new',
            search: { template: template.id, ...carry } as never,
          }),
      }))}
    />
  );
}

// ─── From Search ──────────────────────────────────────────────────────────────

function NewEstimateFromSearch({
  businessUnitId,
}: {
  /**
   * The unit to stamp on. For a create path this is what the gate settled
   * on — the link's unit once verified active, or a single-unit tenant's only
   * unit, which the link never named — and NOT the raw URL value: a unit the
   * gate dropped (archived, or unverifiable after a failed fetch) must not
   * leak back in here. Edit and duplicate pass the URL value through; the
   * record's own unit is what those read.
   */
  businessUnitId: string | undefined;
}) {
  const {
    edit: editId,
    duplicate: duplicateId,
    template: templateFromUrl,
    contactId: prefillContactId,
    locationId: prefillLocationId,
    leadId: prefillLeadId,
    sourceJobId,
    fulfillmentMode,
    originAppointmentId,
  } = Route.useSearch();
  return (
    <EstimateFormPage
      editId={editId}
      duplicateId={duplicateId}
      templateId={templateFromUrl}
      prefillContactId={prefillContactId}
      prefillLocationId={prefillLocationId}
      prefillLeadId={prefillLeadId}
      sourceJobId={sourceJobId}
      fulfillmentMode={fulfillmentMode}
      originAppointmentId={originAppointmentId}
      selectedBusinessUnitId={businessUnitId}
    />
  );
}
